import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { ArrowUpRight } from "lucide-react";
import { getServices } from "@/lib/content";
import { Reveal } from "@/components/site/motion";
import { motion } from "framer-motion";

const FALLBACK = [
  { id: "s1", icon: "Home", title: "Interior Repainting",
    description: "Walls, ceilings, wood & metal. Premium emulsions, dust-free finishes.", order: 1 },
  { id: "s2", icon: "Paintbrush", title: "Exterior Repainting",
    description: "Weather-proof repaints for apartments, independent homes & villas.", order: 2 },
  { id: "s3", icon: "RefreshCw", title: "Full Home Repaint",
    description: "End-to-end repaint with scraping & primer for flawless results.", order: 3 },
  { id: "s4", icon: "Sparkles", title: "Accent Wall Refresh",
    description: "Single-room refresh and feature wall repaints to revive your space.", order: 4 },
  { id: "s5", icon: "Droplets", title: "Damp-Patch Repainting",
    description: "Treat damp patches, peeling and seepage, then repaint for a clean finish.", order: 5 },
];

const spanFor = (i, total) => {
  // bento feel: first and 4th span 2 cols, last spans 3 if fits
  if (i === 0) return "md:col-span-2";
  if (i === 3) return "md:col-span-2";
  if (i === total - 1 && total % 3 === 1) return "md:col-span-3";
  return "";
};

export default function Services() {
  const [items, setItems] = useState(FALLBACK);

  useEffect(() => {
    getServices()
      .then((d) => d?.length && setItems(d))
      .catch(() => {});
  }, []);

  return (
    <section id="services" className="py-20 md:py-28" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <Reveal className="text-xs font-bold tracking-[0.22em] uppercase text-orange-600">
              What we do
            </Reveal>
            <Reveal as="h2" delay={0.05} className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl text-ink">
              Repainting services, end-to-end.
            </Reveal>
            <Reveal delay={0.1} className="mt-4 text-ink-2">
              From a single accent wall to full apartment repaints and exterior
              repaints — one crew, one price, zero surprises.
            </Reveal>
          </div>
          <a
            href="#contact"
            className="hidden md:inline-flex items-center gap-1 text-sm font-bold text-orange-600 hover:text-orange-700"
          >
            Request on-site visit <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {items.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Paintbrush;
            return (
              <motion.article
                key={s.id || s.title}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -4 }}
                className={`group relative overflow-hidden rounded-2xl bg-white hairline shadow-soft p-6 md:p-7 transition-shadow duration-300 hover:shadow-[0_18px_50px_rgba(15,23,42,0.08)] ${spanFor(i, items.length)}`}
              >
                <div className="relative z-10 flex items-start justify-between gap-4">
                  <div className="h-11 w-11 rounded-xl bg-orange-50 grid place-items-center">
                    <Icon className="h-5 w-5 text-orange-600" />
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-muted-ink transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-orange-600" />
                </div>
                <h3 className="relative z-10 mt-5 font-display font-bold text-xl text-ink">
                  {s.title}
                </h3>
                <p className="relative z-10 mt-2 text-sm text-ink-2 leading-relaxed">
                  {s.description}
                </p>
                <a
                  href="#contact"
                  className="relative z-10 mt-5 inline-flex items-center gap-1 text-sm font-bold text-orange-600 hover:text-orange-700"
                  data-testid={`service-learn-more-${i}`}
                >
                  Learn more <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
